import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import SectionTitle from "../ui/SectionTitle";

/**
 * Services Component
 * Displays services in a responsive grid of cards.
 *
 * `.icon-box`, `.icon` and the h4 are load-bearing selectors for e2e/sections.spec.js.
 */

// Icon plus the key naming its copy under Services.items.*. The landing page
// renders <Services /> with no props, so this list is what a visitor actually
// reads — it has to come out of the bundle, not out of this file.
const DEFAULT_SERVICES = [
  { icon: "bi-calendar4-week", key: "onlineBooking" },
  { icon: "bi-bell", key: "automatedReminders" },
  { icon: "bi-people", key: "resourceManagement" },
  { icon: "bi-graph-up-arrow", key: "analyticsReports" },
  { icon: "bi-palette", key: "customizablePages" },
  { icon: "bi-shield-check", key: "secureReliable" },
];

const Services = ({
  sectionTitle = null,
  sectionDescription = null,
  services = [],
}) => {
  const { t } = useTranslation();

  // Null rather than a literal default, so an unset prop falls through to the
  // translation instead of pinning the section to English.
  const title = sectionTitle ?? t("Services.title");
  const description = sectionDescription ?? t("Services.description");

  const servicesToDisplay =
    services.length > 0
      ? services
      : DEFAULT_SERVICES.map(({ icon, key }) => ({
          icon,
          title: t(`Services.items.${key}.title`),
          description: t(`Services.items.${key}.description`),
          link: "",
        }));

  return (
    <section id="services" className="services bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle title={title} description={description} />

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {servicesToDisplay.map((service, index) => (
            <article
              key={index}
              className="icon-box group relative flex flex-col rounded-card border border-ink-200 bg-white p-7 shadow-card transition duration-200 hover:-translate-y-1 hover:border-brand-200 hover:shadow-card-hover motion-reduce:transform-none motion-reduce:transition-none"
            >
              <div className="icon mb-5 inline-flex size-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors duration-200 group-hover:bg-brand-600 group-hover:text-white">
                <i className={`bi ${service.icon} text-2xl`} aria-hidden="true" />
              </div>

              <h4 className="title text-lg font-semibold text-ink-900">
                {service.link ? (
                  <a
                    href={service.link}
                    className="after:absolute after:inset-0 hover:text-brand-700"
                  >
                    {service.title}
                  </a>
                ) : (
                  service.title
                )}
              </h4>

              <p className="description mt-2.5 text-fluid-sm leading-relaxed text-ink-500">
                {service.description}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

Services.propTypes = {
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
  services: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      link: PropTypes.string,
    }),
  ),
};

export default Services;
